import { Balance } from '@/src/experiences/card-payments/screens/MakePayment/Balance'
import { ScrollView } from 'react-native'
import { Button } from '@/src/common/components/Button'
import { Routes } from '@/src/common/enums/routes'
import type { FunctionComponent, ReactNode } from 'react'
import { Text, View } from 'react-native'
import {
  useCardManagementStackScreensAreModals,
  useCardStackScreen,
} from '@/src/app/main/(tabs)/card/card-management/_common/cardStackScreen'
import * as CardPaymentsConstants from '@/src/experiences/card-payments/constants'
import { mesaColorNames, typography } from '@/src/common/theme'
import { Separator, Spinner } from 'tamagui'
import { provideCardPlatform } from '@/src/experiences/card-platform/provideCardPlatform'
import { PlatformMakePayment } from '@/src/experiences/card-payments/screens/MakePayment/PlatformMakePayment'
import { LegacyMakePayment } from '@/src/experiences/card-payments/screens/MakePayment/LegacyMakePayment'
import { isE2E } from '@/src/common/environment'

export function usePaymentCardStackScreenProps() {
  const cardManagementStackScreensAreModals = useCardManagementStackScreensAreModals()
  return useCardStackScreen({
    name: 'payment',
    options: {
      title: 'Make a Payment',
      ...(cardManagementStackScreensAreModals
        ? { presentation: 'transparentModal' }
        : { presentation: 'modal', headerShown: false }),
    },
  })
}

export type PaymentCardStackScreenProps = ReturnType<typeof usePaymentCardStackScreenProps>

export type MakePaymentComponentType = FunctionComponent<
  (
    | {
        loading: true
        statementDueOn?: undefined
        autoPayEntryPoint?: undefined
        oneTimeScheduledTransfers?: undefined
      }
    | {
        loading?: false
        statementDueOn?: string
        autoPayEntryPoint: ReactNode
        oneTimeScheduledTransfers: ReactNode
      }
  ) &
    PaymentCardStackScreenProps
>

const MakePayment = provideCardPlatform<MakePaymentComponentType>(({ platform, ToWrap }) => {
  const paymentCardStackScreenProps = usePaymentCardStackScreenProps()
  switch (platform) {
    case 'HIGHNOTE':
      return <LegacyMakePayment MakePaymentComponent={ToWrap} {...paymentCardStackScreenProps} />
    case 'LOANPRO':
      return <PlatformMakePayment MakePaymentComponent={ToWrap} {...paymentCardStackScreenProps} />
    default:
      platform satisfies undefined
      return <ToWrap loading {...paymentCardStackScreenProps} />
  }
})(
  ({
    statementDueOn,
    loading,
    Modal,
    CardStackLink,
    autoPayEntryPoint,
    oneTimeScheduledTransfers,
  }) =>
    Modal({
      children: (
        <ScrollView
          contentContainerStyle={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16 }}
        >
          <Balance />

          <View
            style={{
              marginVertical: 32,
              backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
              borderRadius: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'center',
              minHeight: 67,
              overflow: 'hidden',
            }}
          >
            {loading ? (
              isE2E ? (
                <></>
              ) : (
                <Spinner color={mesaColorNames['Mesa - Brand/Secondary #223843']} />
              )
            ) : (
              <>
                {autoPayEntryPoint}
                {oneTimeScheduledTransfers ? (
                  <>
                    <Separator
                      style={{
                        marginLeft: 16,
                        marginRight: 16,
                        borderColor: mesaColorNames['global/grey/20 #E6E9EB'],
                      }}
                    />
                    {oneTimeScheduledTransfers}
                  </>
                ) : null}
              </>
            )}
          </View>

          <CardStackLink href={Routes.CardManagementOneTimePayment.route} asChild>
            <Button
              testID={CardPaymentsConstants.Ids.Press.ScheduleOnetimePay}
              title="Schedule One-time payment"
              tag="a"
              style={{ textDecorationLine: 'none' }}
            />
          </CardStackLink>

          {statementDueOn && (
            <Text
              style={[
                typography.S,
                {
                  color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
                  paddingTop: 16,
                  paddingHorizontal: 8,
                  marginBottom: 32,
                },
              ]}
            >
              If we do not receive your Minimum Payment Due by the Payment Due Date of{' '}
              {statementDueOn}, you may have to pay a late fee of up to $8.
            </Text>
          )}
        </ScrollView>
      ),
    }),
)

export const MakePaymentScreen = {
  name: 'auto-pay-schedule',
  Screen: MakePayment,
} as const
